"use client";

import { Button, EmptyTomb } from "@jesus-central-church/ui";
import { motion } from "motion/react";
import { Home, Search } from "lucide-react";

export default function NotFound() {
  return (
    <div className="relative min-h-screen bg-boldness overflow-hidden">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a09] via-boldness to-boldness" />

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-8">
        {/* 404 Number - dramatic display */}
        <motion.div
          className="relative mb-4 md:mb-6"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
        >
          {/* Glow behind 404 */}
          <motion.div
            className="absolute inset-0 blur-3xl"
            style={{
              background:
                "radial-gradient(circle, rgba(200, 150, 87, 0.4) 0%, transparent 70%)",
            }}
            animate={{
              opacity: [0.4, 0.7, 0.4],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          {/* 404 Text */}
          <motion.h1
            className="relative font-heading text-[100px] md:text-[150px] lg:text-[180px] font-bold text-transparent bg-clip-text tracking-tight leading-none"
            style={{
              backgroundImage:
                "linear-gradient(180deg, #edf2e9 0%, #c89657 50%, #746e06 100%)",
            }}
            animate={{
              textShadow: [
                "0 0 40px rgba(200, 150, 87, 0.3)",
                "0 0 80px rgba(200, 150, 87, 0.5)",
                "0 0 40px rgba(200, 150, 87, 0.3)",
              ],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            404
          </motion.h1>
        </motion.div>

        {/* Empty Tomb Scene */}
        <motion.div
          className="w-full max-w-2xl"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <EmptyTomb compact={true} />
        </motion.div>

        {/* Message */}
        <motion.div
          className="mt-6 md:mt-8 text-center max-w-xl px-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h2 className="font-heading text-2xl md:text-3xl text-freedom mb-3 tracking-wide">
            Pagina Niet Gevonden
          </h2>
          <p className="text-freedom/70 text-base md:text-lg leading-relaxed">
            Net als bij het lege graf, is wat je zocht hier niet meer.
            <br className="hidden md:block" />
            <span className="text-strategy-gold font-medium">
              {" "}
              Maar wees niet bevreesd!
            </span>{" "}
            Er is nog zoveel meer te ontdekken.
          </p>
        </motion.div>

        {/* Scripture reference */}
        <motion.div
          className="mt-4 md:mt-6 px-4 py-2 rounded-full bg-strategy-gold/10 border border-strategy-gold/20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.2 }}
        >
          <p className="text-sm text-strategy-gold/80 italic">
            &ldquo;Hij is hier niet, want Hij is opgestaan!&rdquo; — Mattheüs
            28:6
          </p>
        </motion.div>

        {/* Action buttons */}
        <motion.div
          className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
        >
          <Button
            href="/"
            colorScheme="strategy-gold"
            variant="primary"
            size="large"
            showArrowIcon={false}
          >
            <Home className="w-5 h-5 mr-2" />
            Terug naar Home
          </Button>

          <Button
            href="/activiteiten"
            colorScheme="freedom"
            variant="outline"
            size="large"
            showArrowIcon={false}
          >
            <Search className="w-5 h-5 mr-2" />
            Ontdek Activiteiten
          </Button>
        </motion.div>

        {/* Fun footer message */}
        <motion.p
          className="mt-12 text-xs text-freedom/40 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.5 }}
        >
          Tip: Probeer de URL te controleren of gebruik het menu hierboven
        </motion.p>
      </div>

      {/* Decorative elements */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-boldness via-boldness/80 to-transparent" />
      </motion.div>

      {/* Corner decorative glow */}
      <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full bg-strategy-gold/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-64 h-64 rounded-full bg-strategy-red/5 blur-3xl pointer-events-none" />
    </div>
  );
}
